/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    createContext,
    useContext,
    useLayoutEffect,
    type ComponentPropsWithoutRef,
    type CSSProperties,
    type ElementType,
    type ReactElement,
    type ReactNode,
} from 'react';
import {
    useDashboardLayout,
    type DashboardLayoutBreakpoint,
} from '@/context/DashboardLayoutContext';

type DashboardGridBreakpoint = DashboardLayoutBreakpoint;

const DASHBOARD_GRID_BREAKPOINT_CLASS_NAMES: Record<DashboardGridBreakpoint, string> = {
    sm: 'dashboard-grid--sm',
    md: 'dashboard-grid--md',
    lg: 'dashboard-grid--lg',
    xl: 'dashboard-grid--xl',
    '2xl': 'dashboard-grid--2xl',
};

const DASHBOARD_GRID_ITEM_BREAKPOINT_CLASS_NAMES: Record<DashboardGridBreakpoint, string> = {
    sm: 'dashboard-grid__item--sm',
    md: 'dashboard-grid__item--md',
    lg: 'dashboard-grid__item--lg',
    xl: 'dashboard-grid__item--xl',
    '2xl': 'dashboard-grid__item--2xl',
};

interface DashboardGridContextValue {
    breakpoint?: DashboardGridBreakpoint;
}

interface DashboardGridStyle extends CSSProperties {
    '--dashboard-grid-auto-rows'?: string;
    '--dashboard-grid-cols'?: string;
    '--dashboard-grid-rows'?: string;
    '--dashboard-grid-item-mobile-min-height'?: string;
    '--dashboard-grid-col-span'?: string;
    '--dashboard-grid-row-span'?: string;
}

interface DashboardGridProps
    extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
    baseAutoRows?: string;
    breakpoint?: DashboardGridBreakpoint;
    children: ReactNode;
    desktopCols?: number | string[];
    desktopRows?: number | string[];
    mobileMinHeight?: string;
}

type DashboardGridItemProps<T extends ElementType> = {
    as?: T;
    children: ReactNode;
    className?: string;
    desktopColSpan?: number;
    desktopRowSpan?: number;
    mobileMinHeight?: string;
    scrollable?: boolean;
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'children' | 'className'>;

const DashboardGridContext = createContext<DashboardGridContextValue>({});

function joinClassNames(...classNames: Array<string | undefined | false | null>): string {
    return classNames.filter(Boolean).join(' ');
}

function resolveGridTrackDefinition(
    tracks: number | string[] | undefined,
    fallbackTrack = 'minmax(0, 1fr)',
): string | undefined {
    if (typeof tracks === 'number') {
        return `repeat(${tracks}, ${fallbackTrack})`;
    }

    if (Array.isArray(tracks) && tracks.length > 0) {
        return tracks.join(' ');
    }

    return undefined;
}

function DashboardGridRoot({
    baseAutoRows,
    breakpoint,
    children,
    className,
    desktopCols,
    desktopRows,
    mobileMinHeight = '150px',
    style,
    ...props
}: DashboardGridProps) {
    const { setDesktopBreakpoint } = useDashboardLayout();
    const hasDesktopLayout = Boolean(
        breakpoint && (desktopCols || desktopRows),
    );
    const hasFixedDesktopHeight = Boolean(
        breakpoint && desktopCols && desktopRows,
    );
    const desktopColsDefinition = resolveGridTrackDefinition(desktopCols);
    const desktopRowsDefinition = resolveGridTrackDefinition(
        desktopRows,
        'minmax(0, 1fr)',
    );
    const resolvedBaseAutoRows = baseAutoRows ?? `minmax(${mobileMinHeight},auto)`;
    const gridStyle: DashboardGridStyle = {
        ...(style ?? {}),
        '--dashboard-grid-auto-rows': resolvedBaseAutoRows,
        ...(desktopColsDefinition
            ? { '--dashboard-grid-cols': desktopColsDefinition }
            : {}),
        ...(desktopRowsDefinition
            ? { '--dashboard-grid-rows': desktopRowsDefinition }
            : {}),
    };

    useLayoutEffect(() => {
        if (!hasDesktopLayout || !breakpoint) {
            return undefined;
        }

        setDesktopBreakpoint(breakpoint);

        return () => {
            setDesktopBreakpoint(null);
        };
    }, [breakpoint, hasDesktopLayout, setDesktopBreakpoint]);

    return (
        <DashboardGridContext.Provider value={{ breakpoint }}>
            <div
                className={joinClassNames(
                    'dashboard-grid',
                    hasDesktopLayout && breakpoint
                        ? DASHBOARD_GRID_BREAKPOINT_CLASS_NAMES[breakpoint]
                        : undefined,
                    hasFixedDesktopHeight ? 'dashboard-grid--desktop-fill' : undefined,
                    className,
                )}
                style={gridStyle}
                {...props}
            >
                {children}
            </div>
        </DashboardGridContext.Provider>
    );
}

function DashboardGridItem<T extends ElementType = 'div'>({
    as,
    children,
    className,
    desktopColSpan,
    desktopRowSpan,
    mobileMinHeight,
    scrollable = false,
    style,
    ...props
}: DashboardGridItemProps<T>) {
    const { breakpoint } = useContext(DashboardGridContext);
    const Component = (as ?? 'div') as ElementType;
    const hasDesktopSpan = Boolean(
        breakpoint && (desktopColSpan || desktopRowSpan),
    );
    const itemStyle: DashboardGridStyle = {
        ...(style as CSSProperties | undefined),
        ...(mobileMinHeight
            ? { '--dashboard-grid-item-mobile-min-height': mobileMinHeight }
            : {}),
        ...(desktopColSpan
            ? { '--dashboard-grid-col-span': String(desktopColSpan) }
            : {}),
        ...(desktopRowSpan
            ? { '--dashboard-grid-row-span': String(desktopRowSpan) }
            : {}),
    };

    return (
        <Component
            className={joinClassNames(
                mobileMinHeight ? 'dashboard-grid__item--mobile-min-height' : undefined,
                hasDesktopSpan && breakpoint
                    ? DASHBOARD_GRID_ITEM_BREAKPOINT_CLASS_NAMES[breakpoint]
                    : undefined,
                className,
            )}
            style={itemStyle}
            {...props}
        >
            {scrollable ? (
                <div className="dashboard-panel__content-scroll">
                    {children}
                </div>
            ) : children}
        </Component>
    );
}

interface DashboardGridComponent {
    (props: DashboardGridProps): ReactElement;
    Item: typeof DashboardGridItem;
}

const DashboardGrid = Object.assign(
    DashboardGridRoot,
    { Item: DashboardGridItem },
) as DashboardGridComponent;

export default DashboardGrid;
